// @flow

import {toast} from "react-toastify"
import i18n from "i18next"

import {del, put, post} from "../client"
import {
  getItemToInvoice,
  getBabyListInvoiceItem,
  groupItemsToInvoice,
} from "../reducers/baby-list-invoice"
import {transformParams} from "../actions/invoice"
import type {
  BabyListInvoice,
  BabyListItem,
  ThunkAction,
  ItemToInvoice,
  Action,
  GetState,
  Dispatch,
} from "../types"

export const setBabyListInvoice = (
  babyListInvoice: BabyListInvoice
): Action => ({
  type: "SET_BABY_LIST_INVOICE",
  babyListInvoice,
})

export const setSearchItems = (
  searchItems: $ReadOnlyArray<BabyListItem>
): Action => ({
  type: "SET_BABY_LIST_INVOICE_SEARCH_ITEMS",
  searchItems,
})

export const setItemsToInvoice = (
  itemsToInvoice: $ReadOnlyArray<ItemToInvoice>
): Action => ({
  type: "SET_BABY_LIST_INVOICE_ITEMS_TO_INVOICE",
  itemsToInvoice,
})

const setFinaliseInvoiceError = (finaliseError: boolean): Action => ({
  type: "SET_BABY_LIST_INVOICE_FINALISE_ERROR",
  finaliseError,
})

export const deleteBabyListInvoice = (
  babyToken: string,
  invoiceId: number
): ThunkAction => {
  return (dispatch: Dispatch) => {
    dispatch(babyListInvoiceIsProcessing())
    return del(`/babies/${babyToken}/invoices/${invoiceId}`).then(() => {
      window.location.href = `/admin/babies/${window.menageConfig.data.babyId}/invoicing`
      dispatch(babyListInvoiceIsDoneProcessing())
    })
  }
}

export const finaliseBabyListInvoice = (
  babyToken: string,
  invoiceId: number
): ThunkAction => {
  return (dispatch: Dispatch) => {
    dispatch(babyListInvoiceIsProcessing())
    dispatch(setFinaliseInvoiceError(false))
    return post(`/babies/${babyToken}/invoices/${invoiceId}/finalise`).then(
      response => {
        if (response.data) {
          dispatch(setBabyListInvoice(response.data.babyListInvoice))
          toast.success(i18n.t("toast.baby.finaliseBabyListInvoice.success"))
        } else {
          dispatch(setFinaliseInvoiceError(true))
          toast.error(i18n.t("toast.baby.finaliseBabyListInvoice.error"))
        }
        dispatch(babyListInvoiceIsDoneProcessing())
      }
    )
  }
}

export const payBabyListInvoice = (
  babyToken: string,
  invoiceId: number
): ThunkAction => {
  return (dispatch: Dispatch) => {
    dispatch(babyListInvoiceIsProcessing())
    return post(`/babies/${babyToken}/invoices/${invoiceId}/pay`).then(
      response => {
        if (response.data) {
          dispatch(setBabyListInvoice(response.data.babyListInvoice))
          toast.success(i18n.t("toast.baby.payBabyListInvoice.success"))
        } else {
          // data.errors == "invoice_not_valid"
          toast.error(i18n.t("toast.baby.payBabyListInvoice.error"))
        }
        dispatch(babyListInvoiceIsDoneProcessing())
      }
    )
  }
}

export const addItemToBabyListInvoice = (
  babyToken: string,
  babyListInvoiceId: number,
  babyListItemId: number
): ThunkAction => {
  return (dispatch: Dispatch, getState: GetState) => {
    dispatch(babyListInvoiceIsProcessing())
    const {
      babyListInvoice: {itemsToInvoice},
    } = getState()
    const item = getItemToInvoice(itemsToInvoice, babyListItemId)
    const amount = item ? item.amount : 0
    return post(`/babies/${babyToken}/invoices/${babyListInvoiceId}/items`, {
      babyListItemId,
      amount,
      amountDiscount: 0,
    }).then(response => {
      dispatch(setBabyListInvoice(response.data.babyListInvoice))
      dispatch(setSearchItems(response.data.searchItems))
      dispatch(setSuppliers(response.data.suppliers))
      dispatch(
        setItemsToInvoice(groupItemsToInvoice(response.data.itemsToInvoice))
      )
      dispatch(babyListInvoiceIsDoneProcessing())
    })
  }
}

export const updateBabyListInvoiceItem = (
  babyToken: string,
  babyListInvoiceId: number,
  babyListInvoiceItemId: number,
  params: any
): ThunkAction => {
  return (dispatch: Dispatch) => {
    dispatch(babyListInvoiceIsProcessing())
    return put(
      `/babies/${babyToken}/invoices/${babyListInvoiceId}/items/${babyListInvoiceItemId}`,
      params
    ).then(response => {
      dispatch(setBabyListInvoice(response.data.babyListInvoice))
      dispatch(setSearchItems(response.data.searchItems))
      dispatch(
        setItemsToInvoice(groupItemsToInvoice(response.data.itemsToInvoice))
      )
      dispatch(babyListInvoiceIsDoneProcessing())
    })
  }
}

export const updateBabyListInvoiceMeta = (
  babyToken: string,
  babyListInvoiceId: number,
  params: any
): ThunkAction => {
  return (dispatch: Dispatch) => {
    dispatch(babyListInvoiceIsProcessing())
    return put(
      `/babies/${babyToken}/invoices/${babyListInvoiceId}`,
      transformParams(params)
    ).then(response => {
      dispatch(setBabyListInvoice(response.data.babyListInvoice))
      dispatch(babyListInvoiceIsDoneProcessing())
    })
  }
}

export const updateBabyListInvoiceUseGifts = (
  babyToken: string,
  babyListInvoiceId: number,
  use: boolean
): ThunkAction => {
  return (dispatch: Dispatch) => {
    dispatch(babyListInvoiceIsProcessing())
    return put(`/babies/${babyToken}/invoices/${babyListInvoiceId}/gifts`, {
      use,
    }).then(response => {
      dispatch(setBabyListInvoice(response.data.babyListInvoice))
      dispatch(babyListInvoiceIsDoneProcessing())
    })
  }
}

export const updateBabyListInvoiceUseDeposit = (
  babyToken: string,
  babyListInvoiceId: number,
  use: boolean
): ThunkAction => {
  return (dispatch: Dispatch) => {
    dispatch(babyListInvoiceIsProcessing())
    return put(`/babies/${babyToken}/invoices/${babyListInvoiceId}/deposit`, {
      use,
    }).then(response => {
      dispatch(setBabyListInvoice(response.data.babyListInvoice))
      dispatch(babyListInvoiceIsDoneProcessing())
    })
  }
}

export const removeBabyListInvoiceItem = (
  babyToken: string,
  babyListInvoiceId: number,
  babyListInvoiceItemId: number
): ThunkAction => {
  return (dispatch: Dispatch) => {
    dispatch(babyListInvoiceIsProcessing())
    return put(
      `/babies/${babyToken}/invoices/${babyListInvoiceId}/items/${babyListInvoiceItemId}`,
      {amount: 0}
    ).then(response => {
      dispatch(setBabyListInvoice(response.data.babyListInvoice))
      dispatch(setSearchItems(response.data.searchItems))
      dispatch(
        setItemsToInvoice(groupItemsToInvoice(response.data.itemsToInvoice))
      )
      dispatch(babyListInvoiceIsDoneProcessing())
    })
  }
}

export const removeBabyListInvoiceItemByOne = (
  babyToken: string,
  babyListInvoice: BabyListInvoice,
  babyListInvoiceItemId: number
): ThunkAction => {
  return (dispatch: Dispatch) => {
    dispatch(babyListInvoiceIsProcessing())
    const item = getBabyListInvoiceItem(babyListInvoice, babyListInvoiceItemId)
    const amount = item ? item.amount : 1
    return put(
      `/babies/${babyToken}/invoices/${babyListInvoice.id}/items/${babyListInvoiceItemId}`,
      {amount: amount - 1}
    ).then(response => {
      dispatch(setBabyListInvoice(response.data.babyListInvoice))
      dispatch(setSearchItems(response.data.searchItems))
      dispatch(
        setItemsToInvoice(groupItemsToInvoice(response.data.itemsToInvoice))
      )
      dispatch(babyListInvoiceIsDoneProcessing())
    })
  }
}

export const addAllItemsToInvoice = (
  babyToken: string,
  babyListInvoiceId: number
): ThunkAction => {
  return (dispatch: Dispatch) => {
    dispatch(babyListInvoiceIsProcessing())
    return post(
      `/babies/${babyToken}/invoices/${babyListInvoiceId}/items/all`,
      {}
    ).then(response => {
      dispatch(setBabyListInvoice(response.data.babyListInvoice))
      dispatch(setSearchItems(response.data.searchItems))
      dispatch(setSuppliers(response.data.suppliers))
      dispatch(
        setItemsToInvoice(groupItemsToInvoice(response.data.itemsToInvoice))
      )
      dispatch(babyListInvoiceIsDoneProcessing())
    })
  }
}

export const sendBabyListInvoice = (
  babyToken: string,
  babyListInvoiceId: number,
  message: string
): ThunkAction => {
  return (dispatch: Dispatch) => {
    dispatch(babyListInvoiceIsProcessing())
    return post(`/babies/${babyToken}/invoices/${babyListInvoiceId}/send`, {
      message,
    }).then(response => {
      dispatch(setBabyListInvoice(response.data.babyListInvoice))
      dispatch(babyListInvoiceIsDoneProcessing())
      if (!response.data.babyListInvoice) {
        return null
      }
      if (response.data.babyListInvoice.isFinal) {
        toast.success(i18n.t("toast.baby.sendBabyListInvoice.success"))
      } else {
        toast.success(i18n.t("toast.baby.sendBabyListInvoice.success_preview"))
      }
    })
  }
}

const babyListInvoiceIsProcessing = (): Action => ({
  type: "SET_BABY_LIST_INVOICE_PROCESSING",
  isProcessing: true,
})

const babyListInvoiceIsDoneProcessing = (): Action => ({
  type: "SET_BABY_LIST_INVOICE_PROCESSING",
  isProcessing: false,
})

export const setSuppliers = (suppliers: $ReadOnlyArray<any>): Action => ({
  type: "SET_BABY_LIST_INVOICE_SUPPLIERS",
  suppliers,
})
